import React, { useEffect, useState } from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, ThemeProvider,
} from '@mui/material';
import axios from 'axios';
import iziToast from 'izitoast';

import { dropToken, getToken, setToken } from '../../utils/token';
import { internalServerError, LOGIN_ROUTE } from '../../utils/consts/pathRoutes';
import { resetCurrentUser } from '../../utils/currentUser';
import { journalTheme } from '../../utils/consts/journalTheme';

const MIN_LENGTH = 8;

const request = (method, url, data) => axios({
  method,
  url: `${process.env.REACT_APP_URL_PATH}/api/auth/${url}`,
  data,
  headers: { Authorization: getToken() },
});

/**
 * Смена собственного пароля — для любого пользователя, включая роль журнала,
 * у которой раньше такой возможности не было вовсе.
 *
 * Текущий пароль обязателен: иначе украденный токен позволял бы сменить пароль и
 * закрыть владельцу вход в собственную учётку.
 *
 * Сервер после смены отзывает все выданные токены и возвращает новый. Его обязательно
 * нужно сохранить — старый уже не принимается, и без замены пользователя выбросило бы
 * на следующем же запросе.
 */
const ChangePasswordModal = ({ open, onClose }) => {
  const [form, setForm] = useState({ current: '', next: '', repeat: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ current: '', next: '', repeat: '' });
  }, [open]);

  const change = (field) => (event) => setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const tooShort = form.next.length > 0 && form.next.length < MIN_LENGTH;
  const mismatch = form.repeat.length > 0 && form.next !== form.repeat;
  const canSubmit = form.current && form.next.length >= MIN_LENGTH && form.next === form.repeat;

  const submit = () => {
    setSaving(true);
    request('put', 'changePassword', { current_password: form.current, password: form.next })
      .then(({ data }) => {
        setToken(data.token);
        resetCurrentUser();
        iziToast.success({ message: data.message, position: 'topRight' });
        onClose();
      })
      .catch((e) => iziToast.error({ message: internalServerError(e), position: 'topRight' }))
      .finally(() => setSaving(false));
  };

  const logoutEverywhere = () => {
    request('post', 'logoutEverywhere')
      .then(() => {
        dropToken();
        window.location.assign(LOGIN_ROUTE);
      })
      .catch((e) => iziToast.error({ message: internalServerError(e), position: 'topRight' }));
  };

  return (
    <ThemeProvider theme={journalTheme}>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>Смена пароля</DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Текущий пароль" type="password" value={form.current}
              onChange={change('current')} autoComplete="current-password" autoFocus
            />
            <TextField
              label="Новый пароль" type="password" value={form.next}
              onChange={change('next')} autoComplete="new-password"
              error={tooShort}
              helperText={tooShort ? `Не короче ${MIN_LENGTH} символов` : `Не короче ${MIN_LENGTH} символов, не совпадает с логином`}
            />
            <TextField
              label="Повторите новый пароль" type="password" value={form.repeat}
              onChange={change('repeat')} autoComplete="new-password"
              error={mismatch} helperText={mismatch ? 'Пароли не совпадают' : ' '}
            />

            <span className="journal-muted">
              После смены пароля все остальные сеансы завершатся — на других устройствах
              понадобится войти заново.
            </span>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button color="error" onClick={logoutEverywhere}>
            Выйти со всех устройств
          </Button>
          <Stack direction="row" spacing={1}>
            <Button onClick={onClose}>Отмена</Button>
            <Button variant="contained" onClick={submit} disabled={!canSubmit || saving}>
              Сменить
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default ChangePasswordModal;
